/**
 * Video Modal Shared Utility
 * Exposes window.DreoVideoModal.bind(sectionEl) for any section that uses
 * video trigger buttons (data-video-trigger="true") and video-modal snippets.
 *
 * Used by: slideshow-swiper, lp-image-with-text, lp-static-image-comparison
 */
if (!window.DreoVideoModal) {
  window.DreoVideoModal = (function () {
    'use strict';

    const FOCUSABLE =
      'button:not([disabled]), [href], video, iframe, video-media, [tabindex]:not([tabindex="-1"])';
    let lastFocusedTrigger = null;
    let lockedScrollY = null;
    let originalBodyStyles = null;

    function lockBodyScroll() {
      if (lockedScrollY !== null) return;

      lockedScrollY = window.pageYOffset || window.scrollY || 0;
      originalBodyStyles = {
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width,
        height: document.body.style.height,
        overflow: document.body.style.overflow
      };
      // position: fixed 会把 body 视觉上移到顶部；用 top 保存打开弹窗前的位置。
      document.body.style.top = `-${lockedScrollY}px`;
      document.body.classList.add('modal-open');
    }

    function unlockBodyScroll() {
      // 页面上仍有其他共享视频弹窗打开时，不提前解除滚动锁定。
      if (document.querySelector('.video-modal.active')) return;

      const restoreY = lockedScrollY;
      const styles = originalBodyStyles;
      lockedScrollY = null;
      originalBodyStyles = null;
      document.body.classList.remove('modal-open');

      if (styles) {
        document.body.style.position = styles.position;
        document.body.style.top = styles.top;
        document.body.style.width = styles.width;
        document.body.style.height = styles.height;
        document.body.style.overflow = styles.overflow;
      } else {
        document.body.style.top = '';
      }

      if (restoreY !== null) window.scrollTo(0, restoreY);
    }

    function bindVideoModalEvents(section) {
      const videoModals = section.querySelectorAll('.video-modal');

      function isShown(el) {
        return !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));
      }

      /** 弹窗内媒体用 data-src 延迟到打开时才加载；多视口时只加载当前可见那一档 */
      function startMedia(modal) {
        [...modal.querySelectorAll('[data-src]')].forEach((el) => {
          const viewport = el.closest('.video-modal__viewport');
          if (viewport && !isShown(viewport)) return;
          const src = el.getAttribute('data-src');
          if (src && el.getAttribute('src') !== src) el.setAttribute('src', src);
        });
      }

      function syncViewportMedia(modal) {
        modal.querySelectorAll('.video-modal__viewport').forEach((wrap) => {
          const media = wrap.querySelector('video, iframe');
          if (!media) return;
          if (isShown(wrap)) {
            const src = media.getAttribute('data-src');
            if (src && media.getAttribute('src') !== src) media.setAttribute('src', src);
          } else {
            stopMedia(media);
          }
        });
      }

      function focusableItems(modal) {
        return [...modal.querySelectorAll(FOCUSABLE)].filter(
          (el) => !el.hasAttribute('data-focus-guard') && (el.offsetWidth || el.offsetHeight)
        );
      }

      /**
       * 首尾各放一个可聚焦哨兵：焦点走到哨兵就送回弹窗另一端。
       * 用哨兵而不是拦 keydown，因为焦点进入 YouTube/Vimeo iframe 后
       * Tab 事件只在 iframe 内部触发，父页面收不到。
       */
      function addFocusGuards(modal) {
        if (modal.querySelector('[data-focus-guard]')) return;
        ['start', 'end'].forEach((pos) => {
          const guard = document.createElement('span');
          guard.setAttribute('data-focus-guard', pos);
          guard.setAttribute('tabindex', '0');
          guard.setAttribute('aria-hidden', 'true');
          guard.style.cssText = 'position:absolute;width:1px;height:1px;opacity:0;';
          guard.addEventListener('focus', () => {
            const items = focusableItems(modal);
            if (!items.length) return;
            items[pos === 'start' ? items.length - 1 : 0].focus();
          });
          modal[pos === 'start' ? 'prepend' : 'append'](guard);
        });
      }

      function stopMedia(mediaElement) {
        if (mediaElement.tagName === 'VIDEO') {
          mediaElement.pause();
          mediaElement.currentTime = 0;
        } else if (mediaElement.tagName === 'IFRAME') {
          if (mediaElement.hasAttribute('src')) {
            mediaElement.setAttribute('data-src', mediaElement.getAttribute('src'));
            mediaElement.setAttribute('src', 'about:blank');
          }
        }
      }

      function closeModal(modal) {
        if (!modal) return;
        modal.classList.remove('active');
        const modalContent = modal.querySelector('.video-modal__content');
        if (modalContent) {
          [...modalContent.querySelectorAll('video, iframe')].forEach(stopMedia);
        }
        unlockBodyScroll();

        if (lastFocusedTrigger && typeof lastFocusedTrigger.focus === 'function') {
          lastFocusedTrigger.focus({ preventScroll: true });
        }
        lastFocusedTrigger = null;
      }

      function openModal(modalId, trigger) {
        const targetModal = document.getElementById(modalId);
        if (!targetModal) return;
        lastFocusedTrigger = trigger || null;
        targetModal.classList.add('active');
        lockBodyScroll();
        startMedia(targetModal);

        section.querySelectorAll('video').forEach((video) => {
          if (!targetModal.contains(video) && !video.paused) video.pause();
        });

        const closeButton = targetModal.querySelector('.video-modal__close');
        if (closeButton) closeButton.focus({ preventScroll: true });
      }

      // 初始化弹窗关闭事件
      videoModals.forEach((modal) => {
        const closeButton = modal.querySelector('.video-modal__close');
        if (closeButton) {
          closeButton.addEventListener('click', () => closeModal(modal));
        }
        modal.addEventListener('click', (e) => {
          if (e.target === modal) closeModal(modal);
        });
        addFocusGuards(modal);
      });

      // ESC 键关闭
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const activeModal = section.querySelector('.video-modal.active');
          if (activeModal) closeModal(activeModal);
        }
      });

      // 视频触发器
      const videoTriggers = section.querySelectorAll('[data-video-trigger="true"]');
      videoTriggers.forEach((trigger) => {
        trigger.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          const modalId = this.getAttribute('data-target');
          if (modalId) openModal(modalId, this);
        });
      });

      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const activeModal = section.querySelector('.video-modal.active');
          if (activeModal && activeModal.querySelector('.video-modal__viewport')) {
            syncViewportMedia(activeModal);
          }
        }, 150);
      });
    }

    return { bind: bindVideoModalEvents };
  })();
}
